import { SimpleSEO } from '@/components/seo/SimpleSEO';

export function TermsOfService() {
  return (
    <>
      <SimpleSEO
        title="Terms of Service | MdbApp"
        description="Terms of Service for MdbApp Token Creator - Read our terms and conditions"
        keywords="terms of service, terms and conditions, MdbApp terms"
      />
      
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last Updated: October 16, 2025</p>

        <div className="space-y-8 text-foreground">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Agreement to Terms</h2>
            <p className="mb-4">
              By accessing or using MdbApp ("Service," "we," "us," or "our"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these Terms, you may not access the Service.
            </p>
            <p>
              These Terms apply to all visitors, users, and others who access or use the Service, including the mobile application and website at mdbapp.io.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
            <p className="mb-4">
              MdbApp provides a platform for creating, deploying, and managing ERC20/BEP20 tokens on various blockchain networks including Ethereum, BNB Smart Chain, and Avalanche. Our Service includes:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Token creation and deployment tools</li>
              <li>Smart contract management interfaces</li>
              <li>Token burning and liquidity management features</li>
              <li>Contract scanning and security analysis tools</li>
              <li>Token logo management</li>
              <li>Educational guides and documentation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. Eligibility</h2>
            <p className="mb-4">
              To use our Service, you must:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Be at least 18 years of age</li>
              <li>Have the legal capacity to enter into binding contracts</li>
              <li>Not be prohibited from using the Service under applicable laws</li>
              <li>Comply with all local, state, national, and international laws and regulations</li>
              <li>Own or have authorization to use the cryptocurrency wallet you connect</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. User Responsibilities</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">4.1 Wallet Security</h3>
            <p className="mb-4">You are solely responsible for:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Maintaining the security of your cryptocurrency wallet</li>
              <li>Protecting your private keys, seed phrases, and passwords</li>
              <li>All transactions initiated from your wallet</li>
              <li>Any losses resulting from unauthorized access to your wallet</li>
            </ul>
            <p className="mb-4">
              <strong>Important:</strong> We never ask for your private keys or seed phrases. Never share these with anyone.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">4.2 Token Creation and Management</h3>
            <p className="mb-4">When using our token creation services, you agree to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Comply with all applicable securities laws and regulations</li>
              <li>Not create tokens for illegal purposes or fraudulent schemes</li>
              <li>Not create tokens that infringe on intellectual property rights</li>
              <li>Conduct your own legal and regulatory due diligence</li>
              <li>Be responsible for the tokens you create and their use</li>
              <li>Understand the technical and financial risks involved</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">4.3 Prohibited Activities</h3>
            <p className="mb-4">You agree NOT to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Create tokens designed to defraud or deceive others</li>
              <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Use automated systems (bots, scripts) without permission</li>
              <li>Reverse engineer or attempt to extract source code</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Impersonate any person or entity</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Fees and Payments</h2>
            <p className="mb-4">
              Using MdbApp involves various fees:
            </p>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">5.1 Service Fees</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Token creation fees (paid in the native blockchain currency)</li>
              <li>Feature usage fees as displayed in the application</li>
              <li>Fees are subject to change with notice</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">5.2 Blockchain Fees</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Gas fees required by the blockchain network</li>
              <li>These fees are paid directly to network validators, not to MdbApp</li>
              <li>Gas fees vary based on network congestion</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">5.3 No Refunds</h3>
            <p className="mb-4">
              All fees are non-refundable. Once a transaction is confirmed on the blockchain, it cannot be reversed. You are responsible for ensuring transaction details are correct before confirmation.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Intellectual Property</h2>
            <p className="mb-4">
              The Service and its original content, features, and functionality are owned by MdbApp and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
            <p className="mb-4">
              You may not:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Copy, modify, or distribute our proprietary code or designs</li>
              <li>Use our trademarks or branding without permission</li>
              <li>Create derivative works based on our Service</li>
              <li>Remove or alter any copyright notices</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Disclaimers and Limitations of Liability</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">7.1 "AS IS" Service</h3>
            <p className="mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Warranties of merchantability or fitness for a particular purpose</li>
              <li>Warranties of non-infringement</li>
              <li>Warranties that the Service will be uninterrupted or error-free</li>
              <li>Warranties regarding the accuracy or reliability of information</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">7.2 Cryptocurrency Risks</h3>
            <p className="mb-4">
              You acknowledge and accept the following risks:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Cryptocurrency markets are highly volatile and unpredictable</li>
              <li>Token values can decrease to zero</li>
              <li>Smart contracts may contain bugs or vulnerabilities</li>
              <li>Blockchain transactions are irreversible</li>
              <li>Regulatory frameworks are evolving and uncertain</li>
              <li>You may lose all funds invested in tokens</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">7.3 No Investment Advice</h3>
            <p className="mb-4">
              MdbApp does not provide investment, financial, legal, or tax advice. All content is for informational purposes only. You should consult with qualified professionals before making any financial decisions.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">7.4 Limitation of Liability</h3>
            <p className="mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, MDBAPP SHALL NOT BE LIABLE FOR:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, revenue, data, or use</li>
              <li>Loss of cryptocurrency or tokens</li>
              <li>Damages resulting from smart contract bugs or exploits</li>
              <li>Damages resulting from third-party actions or services</li>
              <li>Damages exceeding the fees you paid to MdbApp in the past 12 months</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Indemnification</h2>
            <p className="mb-4">
              You agree to indemnify, defend, and hold harmless MdbApp, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses (including legal fees) arising from:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Tokens you create using our Service</li>
              <li>Your violation of applicable laws or regulations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Third-Party Services</h2>
            <p className="mb-4">
              Our Service integrates with third-party services including:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Wallet providers (MetaMask, WalletConnect)</li>
              <li>Blockchain networks (Ethereum, BSC, Avalanche)</li>
              <li>Blockchain explorers (Etherscan, BscScan, SnowTrace)</li>
            </ul>
            <p>
              We are not responsible for the availability, accuracy, or content of these third-party services. Your use of third-party services is governed by their respective terms and policies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Termination</h2>
            <p className="mb-4">
              We reserve the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Suspend or terminate your access to the Service at any time</li>
              <li>Refuse service to anyone for any reason</li>
              <li>Modify or discontinue the Service without notice</li>
            </ul>
            <p className="mb-4">
              You may stop using the Service at any time by disconnecting your wallet.
            </p>
            <p>
              Upon termination, all provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">11. Governing Law and Dispute Resolution</h2>
            <p className="mb-4">
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which MdbApp operates, without regard to conflict of law provisions.
            </p>
            <p className="mb-4">
              Any disputes arising from these Terms or your use of the Service shall be resolved through:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Good faith negotiations between the parties</li>
              <li>Binding arbitration if negotiations fail</li>
              <li>The courts of competent jurisdiction as a last resort</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">12. Changes to Terms</h2>
            <p className="mb-4">
              We reserve the right to modify these Terms at any time. We will notify you of material changes by:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Posting the updated Terms on this page</li>
              <li>Updating the "Last Updated" date</li>
              <li>Providing notice through the Service or via email</li>
            </ul>
            <p>
              Your continued use of the Service after changes are posted constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">13. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">14. Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and MdbApp regarding the Service and supersede all prior agreements and understandings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">15. Contact Information</h2>
            <p className="mb-4">
              If you have questions about these Terms, please contact us:
            </p>
            <ul className="list-none space-y-2">
              <li><strong>Website:</strong> https://mdbapp.io</li>
              <li><strong>Twitter:</strong> @MobDaBoss</li>
            </ul>
          </section>

          <section className="border-t pt-8 mt-8 bg-yellow-500/10 p-6 rounded-lg">
            <h2 className="text-2xl font-semibold mb-4 text-yellow-600">⚠️ Important Acknowledgment</h2>
            <p className="mb-4">
              BY USING MDBAPP, YOU ACKNOWLEDGE THAT:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>You understand the risks associated with cryptocurrency and blockchain technology</li>
              <li>You are solely responsible for your use of the Service and any tokens you create</li>
              <li>You have read and understood these Terms of Service</li>
              <li>You have consulted with appropriate professionals regarding legal and financial matters</li>
              <li>You accept all risks and agree to hold MdbApp harmless</li>
            </ul>
          </section>
        </div>
      </div>
    </>
  );
}
